/**
 * Modale accessible : focus piégé, fermeture par Échap, rôle `dialog`.
 *
 * Utilisée pour les confirmations d'actions irréversibles et pour la
 * réauthentification forte.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  title,
  description,
  open,
  onClose,
  children,
  actions,
  closeLabel = "Annuler",
}: {
  title: string;
  description?: ReactNode;
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
  actions?: ReactNode;
  closeLabel?: string;
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!open) return;
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((element) => element.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [open, onClose],
  );

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", handleKeyDown);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Donner le focus au premier élément interactif de la modale.
    const timer = window.setTimeout(() => {
      const dialog = dialogRef.current;
      const focusable = dialog?.querySelector<HTMLElement>(FOCUSABLE);
      (focusable ?? dialog)?.focus();
    }, 10);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;
      window.clearTimeout(timer);
      previousFocus.current?.focus?.();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        ref={dialogRef}
        tabIndex={-1}
      >
        <h2 className="modal__title" id={titleId}>
          {title}
        </h2>
        {description && (
          <div id={descriptionId} className="text-muted">
            {description}
          </div>
        )}
        {children}
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {closeLabel}
          </button>
          {actions}
        </div>
      </div>
    </div>
  );
}
